import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/Button";
import { instructorService } from "../services/instructorService";
import {
  ArrowLeft,
  Loader2,
  Mail,
  Calendar,
  Linkedin,
  Twitter,
  Youtube,
  Globe,
  BookOpen,
  Users,
  Award,
  Star,
} from "lucide-react";

// Components
const StatCard = ({
  icon: Icon,
  label,
  value,
  colorClass,
}: {
  icon: any;
  label: string;
  value: string | number;
  colorClass: string;
}) => (
  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
    <div className={`p-3 rounded-lg ${colorClass} bg-opacity-10`}>
      <Icon className={`w-6 h-6 ${colorClass.replace("bg-", "text-")}`} />
    </div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const CourseCard = ({ course }: { course: any }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/courses/${course._id}`)}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
    >
      <div className="h-40 bg-gray-100 relative overflow-hidden">
        {course.thumbnail ? (
          <img
            src={course.thumbnail}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <BookOpen size={32} />
          </div>
        )}
        {course.isPublished ? (
          <span className="absolute top-3 right-3 bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
            Published
          </span>
        ) : (
          <span className="absolute top-3 right-3 bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded">
            Draft
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">
          {course.title}
        </h3>
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <Users size={14} />
            {course.enrollmentCount || 0} students
          </span>
          <span className="flex items-center gap-1">
            <Star size={14} className="text-yellow-400 fill-yellow-400" />
            {course.averageRating || 0}
          </span>
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
          <span className="text-sm font-medium text-gray-900">
            {course.isFree
              ? "Free"
              : `$${course.discountPrice || course.price}`}
          </span>
          <span className="text-xs text-gray-500 capitalize">
            {course.level}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function InstructorDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [instructor, setInstructor] = useState<any>(null);
  const [courses, setCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      // Fetch instructor details and their courses in parallel
      const [instructorRes, coursesRes] = await Promise.all([
        instructorService.getInstructorById(id),
        instructorService.getInstructorCourses(id),
      ]);

      setInstructor(instructorRes.instructor);
      setCourses(coursesRes.courses || []);
    } catch (err: any) {
      console.error("Failed to fetch instructor data:", err);
      setError("Failed to load instructor information.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#e07828] mb-4" />
          <p className="text-gray-500">Loading instructor profile...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !instructor) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] max-w-lg mx-auto text-center">
          <div className="bg-red-50 p-4 rounded-full mb-4">
            <Users className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Instructor Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            {error ||
              "The instructor you are looking for does not exist or has been removed."}
          </p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const user = instructor.user || {};
  const stats = instructor.statistics || {};
  const social = instructor.socialMediaLinks || {};

  return (
    <DashboardLayout>
      <div className="bg-[#FCFCFC] -m-4 p-4 sm:-m-8 sm:p-8 min-h-screen">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        </div>

        {/* Header Profile Section */}
        <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-gray-200 mb-8">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Profile Image */}
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-100 shrink-0 mx-auto md:mx-0">
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.firstName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50">
                  <Users size={48} />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-[#102040] mb-1">
                    {user.firstName} {user.lastName}
                  </h1>
                  <p className="text-[#e07828] font-medium text-lg">
                    {instructor.professionalTitle || "Instructor"}
                  </p>
                </div>
                {/* Action Buttons could go here (e.g. Edit) */}
                <Button
                  onClick={() =>
                    navigate(`/instructors/edit/${instructor._id}`)
                  }
                  variant="outline"
                >
                  Edit Profile
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-600">
                  <Mail size={18} className="text-gray-400" />
                  <span className="text-sm">{user.email}</span>
                </div>
                {user.phoneNumber && (
                  <div className="flex items-center justify-center md:justify-start gap-2 text-gray-600">
                    <div className="w-[18px] text-center">📞</div>
                    <span className="text-sm">{user.phoneNumber}</span>
                  </div>
                )}
                {instructor.createdAt && (
                  <div className="flex items-center justify-center md:justify-start gap-2 text-gray-600">
                    <Calendar size={18} className="text-gray-400" />
                    <span className="text-sm">
                      Joined{" "}
                      {new Date(instructor.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Social Links */}
              <div className="flex items-center justify-center md:justify-start gap-3">
                {social.linkedin && (
                  <a
                    href={social.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Linkedin size={20} />
                  </a>
                )}
                {social.twitter && (
                  <a
                    href={social.twitter}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-sky-50 text-sky-500 rounded-lg hover:bg-sky-100 transition-colors"
                  >
                    <Twitter size={20} />
                  </a>
                )}
                {social.youtube && (
                  <a
                    href={social.youtube}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Youtube size={20} />
                  </a>
                )}
                {social.website && (
                  <a
                    href={social.website}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Globe size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <StatCard
            icon={BookOpen}
            label="Total Courses"
            value={stats.totalCourses || courses.length || 0}
            colorClass="bg-blue-500 text-blue-500"
          />
          <StatCard
            icon={Users}
            label="Students"
            value={stats.totalStudents || 0}
            colorClass="bg-[#e07828] text-[#e07828]"
          />
          <StatCard
            icon={Award}
            label="Experience"
            value={`${instructor.yearsOfExperience || 0} Years`}
            colorClass="bg-purple-500 text-purple-500"
          />
          <StatCard
            icon={Star}
            label="Avg. Rating"
            value={instructor.averageRating || "N/A"}
            colorClass="bg-yellow-400 text-yellow-400"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Biography */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-[#102040] mb-4">About</h2>
              <div className="prose max-w-none text-gray-600 leading-relaxed whitespace-pre-line">
                {instructor.biography || "No biography available."}
              </div>
            </div>

            {/* Courses List */}
            <div>
              <h2 className="text-xl font-bold text-[#102040] mb-4">
                Courses ({courses.length})
              </h2>
              {courses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {courses.map((course) => (
                    <CourseCard key={course._id} course={course} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No courses assigned yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-6">
            {/* Expertise Areas */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-[#102040] mb-4">
                Areas of Expertise
              </h3>
              <div className="flex flex-wrap gap-2">
                {instructor.expertiseAreas &&
                instructor.expertiseAreas.length > 0 ? (
                  instructor.expertiseAreas.map(
                    (area: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {area}
                      </span>
                    )
                  )
                ) : (
                  <span className="text-gray-500 text-sm">Not specified</span>
                )}
              </div>
            </div>

            {/* Additional Info / Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-[#102040] mb-4">Status</h3>
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">Account Status</span>
                <span
                  className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                    instructor.approvalStatus === "approved"
                      ? "bg-green-100 text-green-700"
                      : instructor.approvalStatus === "rejected"
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {instructor.approvalStatus}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">Last Updated</span>
                <span className="text-gray-900 text-sm">
                  {new Date(instructor.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
